import type { ReactNode } from "react";
import {
  IconArrowRight,
  IconFileText,
  IconLayoutGrid,
  IconPlus,
  IconReportAnalytics,
  IconSparkles,
} from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { Button, Card, PageHeader, Text } from "@/components/ui";
import { toastErrorApi } from "@/lib/toast";
import {
  cargarPredefinido,
  cargarTemplate,
  type RepDefinicion,
} from "../services/reporteria.api";
import { usePredefinidos, useTemplates } from "../hooks/use-reporteria";
import { IconoReporte } from "../utils/reporte-iconos";
import { ENCABEZADO_REPORTERIA, SeccionesReporteriaSkeleton } from "./ReporteriaInicioSkeleton";

/**
 * Pantalla de inicio de Reportería (Fase 1 · reportes curados primero). En vez
 * de abrir el builder en blanco, el usuario ve una GALERÍA de reportes listos
 * (predefinidos del admin + sus plantillas) y elige uno con un clic; el builder
 * libre queda detrás de "Crear reporte personalizado" (modo avanzado). Esto baja
 * la carga cognitiva para el 80% que solo quiere abrir un reporte conocido.
 */
export function ReporteriaInicio({
  onAbrir,
  onCrear,
  onVerGenerados,
  puedeCrear = true,
}: {
  onAbrir: (def: RepDefinicion, nombre: string) => void;
  onCrear: () => void;
  onVerGenerados: () => void;
  /** Si es false (rol Visor · solo lectura), se ocultan las acciones de CREAR
   *  reportes personalizados; solo puede abrir y ver los predefinidos (y
   *  exportarlos, que sí está permitido para quien accede al módulo). */
  puedeCrear?: boolean;
}) {
  const predefinidos = usePredefinidos();
  const templates = useTemplates();

  const predefs = predefinidos.data ?? [];
  const tpls = templates.data ?? [];

  const cargarPre = useMutation({
    mutationFn: cargarPredefinido,
    onSuccess: (def, id) => onAbrir(def, predefs.find((p) => p.id === id)?.nombre ?? "Reporte"),
    onError: toastErrorApi,
  });
  const cargarTpl = useMutation({
    mutationFn: cargarTemplate,
    onSuccess: (def, id) => onAbrir(def, tpls.find((t) => t.id === id)?.nombre ?? "Reporte"),
    onError: toastErrorApi,
  });
  const cargando = cargarPre.isPending || cargarTpl.isPending;

  const listasCargando = predefinidos.isLoading || templates.isLoading;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6 p-4 md:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <PageHeader
          title={ENCABEZADO_REPORTERIA.titulo}
          description={ENCABEZADO_REPORTERIA.descripcion}
        />
        <div className="flex flex-wrap gap-2">
          <Button variant="subtle" leftSection={<IconReportAnalytics size={16} />} onClick={onVerGenerados}>
            Mis reportes generados
          </Button>
          {puedeCrear && (
            <Button leftSection={<IconPlus size={16} />} onClick={onCrear}>
              Crear reporte personalizado
            </Button>
          )}
        </div>
      </div>

      {listasCargando ? (
        <SeccionesReporteriaSkeleton puedeCrear={puedeCrear} />
      ) : (
        <>
          {/* Reportes predefinidos (curados por el admin) */}
          <section className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <IconLayoutGrid size={18} className="text-multiva-600" />
              <Text fw={600}>Reportes predefinidos</Text>
            </div>
            {predefs.length === 0 ? (
              <Card padding="lg">
                <Text size="sm" c="dimmed">
                  Todavía no hay reportes predefinidos para tu perfil. Un administrador puede
                  publicarlos desde la configuración de reportería, o puedes crear el tuyo con
                  “Crear reporte personalizado”.
                </Text>
              </Card>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {predefs.map((p) => (
                  <ReporteCard
                    key={`p-${p.id}`}
                    icon={<IconoReporte nombre={p.nombre} />}
                    titulo={p.nombre}
                    descripcion={p.descripcion}
                    disabled={cargando}
                    onClick={() => cargarPre.mutate(p.id)}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Plantillas propias del usuario */}
          <section className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <IconFileText size={18} className="text-multiva-600" />
              <Text fw={600}>Mis plantillas</Text>
            </div>
            {tpls.length === 0 ? (
              <Card padding="lg">
                <Text size="sm" c="dimmed">
                  Cuando guardes un reporte como plantilla aparecerá aquí para reabrirlo con un clic.
                </Text>
              </Card>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {tpls.map((t) => (
                  <ReporteCard
                    key={`t-${t.id}`}
                    icon={<IconFileText size={18} className="text-multiva-600" />}
                    titulo={t.nombre}
                    descripcion={t.descripcion}
                    disabled={cargando}
                    onClick={() => cargarTpl.mutate(t.id)}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Crear personalizado · modo avanzado (oculto para roles de solo lectura) */}
          {puedeCrear && (
            <button
              type="button"
              onClick={onCrear}
              className="flex items-center gap-3 rounded-lg border border-dashed border-border p-4 text-left transition-colors hover:border-multiva-400 hover:bg-multiva-50 dark:hover:bg-multiva-900/20"
            >
              <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-multiva-600 text-white">
                <IconSparkles size={20} />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold">Crear reporte personalizado</span>
                <span className="block text-xs text-muted-foreground">
                  Modo avanzado: elige columnas, filtra, cruza tablas y exporta a Excel.
                </span>
              </span>
              <IconArrowRight size={18} className="ml-auto shrink-0 text-muted-foreground" />
            </button>
          )}
        </>
      )}
    </div>
  );
}

/** Tarjeta grande y clickeable de un reporte guardado. */
function ReporteCard({
  icon,
  titulo,
  descripcion,
  disabled,
  onClick,
}: {
  icon: ReactNode;
  titulo: string;
  descripcion: string | null;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="group flex h-full flex-col gap-2 rounded-lg border border-border bg-card p-4 text-left transition-colors hover:border-multiva-400 hover:bg-multiva-50 disabled:opacity-60 dark:hover:bg-multiva-900/20"
    >
      <div className="flex items-center justify-between">
        {icon}
        <IconArrowRight
          size={16}
          className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
        />
      </div>
      <span className="text-sm font-semibold leading-snug">{titulo}</span>
      {descripcion ? (
        <span className="line-clamp-3 text-xs text-muted-foreground">{descripcion}</span>
      ) : (
        <span className="text-xs text-muted-foreground/70">Sin descripción</span>
      )}
    </button>
  );
}
